/**
 * Button Component — HMVMReviewer Design System Phase 1
 * 
 * Features:
 * - Variants: primary, secondary, danger, ghost
 * - States: default, hover, focus, disabled, loading
 * - Sizes: sm, md, lg
 * - Full accessibility (ARIA, keyboard navigation, focus rings)
 * - Uses semantic theme tokens (product accent, semantic colors)
 * - Square geometry (border-radius: 0)
 */

import React, { ButtonHTMLAttributes, forwardRef } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: `
    bg-[var(--reviewer-btn-primary-bg)]
    text-[var(--reviewer-btn-primary-text)]
    border-[var(--reviewer-btn-primary-border)]
    hover:bg-[var(--reviewer-btn-primary-hover-bg)]
    hover:enabled:opacity-90
  `,
  secondary: `
    bg-[var(--reviewer-btn-secondary-bg)]
    text-[var(--reviewer-btn-secondary-text)]
    border-[var(--reviewer-btn-secondary-border)]
    hover:bg-[var(--reviewer-btn-secondary-hover-bg)]
    hover:enabled:border-[var(--reviewer-border-hover)]
  `,
  danger: `
    bg-[var(--reviewer-btn-danger-bg)]
    text-[var(--reviewer-btn-danger-text)]
    border-[var(--reviewer-btn-danger-border)]
    hover:bg-[var(--reviewer-btn-danger-hover-bg)]
    hover:enabled:opacity-90
  `,
  ghost: `
    bg-transparent
    text-[var(--reviewer-text)]
    border-[var(--reviewer-border)]
    hover:bg-[var(--reviewer-surface-hover)]
    hover:enabled:border-[var(--reviewer-border-hover)]
  `,
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-[var(--reviewer-text-sm)] min-h-[32px]',
  md: 'px-4 py-2 text-[var(--reviewer-text-base)] min-h-[40px]',
  lg: 'px-6 py-3 text-[var(--reviewer-text-md)] min-h-[48px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'secondary',
  size = 'md',
  loading = false,
  fullWidth = false,
  disabled,
  className = '',
  children,
  ...props
}, ref) => {
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      disabled={isDisabled}
      className={`
        inline-flex items-center justify-center gap-2
        border font-medium font-mono
        transition-all duration-150
        focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--reviewer-focus-ring)] focus-visible:outline-offset-2
        disabled:opacity-50 disabled:cursor-not-allowed
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  );
});

Button.displayName = 'Button';
